/**
 * Helpers to start an OAuth redirect that ESCAPES the preview iframe.
 *
 * OAuth providers (Microsoft, Google) send `X-Frame-Options: DENY` /
 * `frame-ancestors 'none'`, so their sign-in pages cannot be loaded in an iframe.
 * In the Emergent preview the app runs inside a sandboxed iframe that also blocks
 * top-frame navigation, so the only reliable way to reach the provider is to open
 * a brand-new top-level browser tab. In production (not iframed) we just redirect
 * the current tab as normal.
 */
export function isIframed() {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true; // cross-origin access threw → we are framed
  }
}

/**
 * Redirect to an OAuth URL that is known up-front (e.g. SSO login).
 * - Not iframed → normal same-tab redirect.
 * - Iframed → open the provider in a new top-level tab (avoids X-Frame-Options).
 */
export function redirectTop(url) {
  if (isIframed()) {
    const w = window.open(url, "_blank", "noopener");
    if (w) return;
    // Popups blocked — last-resort attempt to navigate the top frame.
    try {
      window.top.location.href = url;
      return;
    } catch (_) {
      /* fall through */
    }
  }
  window.location.href = url;
}

/**
 * For async flows where the OAuth URL is fetched first (e.g. "Connect" buttons).
 * Call this SYNCHRONOUSLY inside the click handler (so the popup isn't blocked),
 * then call `.go(url)` once the URL resolves, or `.fail()` on error.
 */
export function openOAuthTab() {
  if (!isIframed()) {
    return {
      go: (url) => { window.location.href = url; },
      fail: () => {},
    };
  }
  const w = window.open("about:blank", "_blank");
  return {
    go: (url) => {
      if (w) w.location.href = url;
      else window.location.href = url; // popup blocked fallback
    },
    fail: () => { if (w) w.close(); },
  };
}
